import { ProfileDestination, ProfileDestinationType } from '~/models/ProfileDestination'
import { ProfileService } from '~/passporter-services/profile/service'
import { useProfileStore } from '~/store/profile'
import EventsService from '~/services/events'
import { TranslationService } from '~/services/Translation'
import { Destination2 } from '~/models/Destination'
import {
	DialogController,
	PredefinedDialog,
	PredefinedDialogType,
} from '~/components/passporterUi/Dialog/dialogController'
import DownloadAppDialog from '~/components/profile/profileDestinations/DownloadAppDialog.vue'
import { ProfileMetric } from '~/actions/metrics/profile'
import { ColorPalette } from '~/common/passporter-styles/themes/color'

export class ProfileDestinationsActions {
	public static async uploadDestination(
		profileId: string,
		destinations: ProfileDestination[]
	): Promise<ProfileDestination[]> {
		return this.addDestinationsToProfile(profileId, destinations)
	}

	public static async addDestinations(
		profileId: string,
		destinations: Destination2[],
		type: ProfileDestinationType
	): Promise<ProfileDestination[]> {
		const profileDestinations = this.toProfileDestinations(destinations, type)
		return this.addDestinationsToProfile(profileId, profileDestinations)
	}

	static async getDestinationsByType(
		profileId: string,
		type: ProfileDestinationType
	): Promise<ProfileDestination[]> {
		try {
			const stored = this.profileStore.getDestinationsByType(type)
			if (stored?.length && this.isCurrentUser(profileId)) return stored

			const destinations = await ProfileService.getDestinations(profileId, type)

			if (this.isCurrentUser(profileId)) this.profileStore.addDestinationsByType(destinations, type)
			return destinations
		} catch (e) {
			console.error(e)
			return []
		}
	}

	static async getDestinations(profileId: string): Promise<ProfileDestination[]> {
		try {
			const stored = this.profileStore.getDestinations()
			if (stored?.length && this.isCurrentUser(profileId)) return stored

			const destinations = await ProfileService.getDestinations(profileId)

			if (this.isCurrentUser(profileId)) this.profileStore.addDestinations(destinations)
			return destinations
		} catch (e) {
			console.error(e)
			return []
		}
	}

	static async resetDestinations(
		profileId: string,
		type: ProfileDestinationType
	): Promise<ProfileDestination[]> {
		if (this.isCurrentUser(profileId)) this.profileStore.resetDestinationsByType(type)
		try {
			return await this.getDestinationsByType(profileId, type)
		} catch (e) {
			console.error(e)
			return []
		}
	}

	public static async removeDestination(
		profileId: string,
		profileDestination: ProfileDestination
	): Promise<void> {
		const t: TranslationService = TranslationService.getInstance()
		try {
			await ProfileService.removeDestination(profileId, profileDestination.id!)
			EventsService.emitSuccess(t.translate('web.profile.toast.remove_success'))
			ProfileMetric.deleteProfileDestination(profileDestination)
		} catch (e) {
			console.error(e)
			EventsService.emitError(t.translate('web.profile.toast.remove_error'))
		}
	}

	public static createDownloadAppDialog(isXS: boolean): DialogController {
		const dialogSetup = new PredefinedDialog(PredefinedDialogType.EMPTY, DownloadAppDialog)
			.props({ isXS })
			.size('s')
			.mobileType('bottomSheet')
			.build()
		return new DialogController(dialogSetup)
	}

	private static toProfileDestinations(
		destinations: Destination2[],
		type: ProfileDestinationType
	): ProfileDestination[] {
		return destinations.map((destination) => ({
			destinationId: destination.id!,
			type,
			name: destination.name,
		}))
	}

	private static async addDestinationsToProfile(
		profileId: string,
		destinations: ProfileDestination[]
	): Promise<ProfileDestination[]> {
		const translation: TranslationService = TranslationService.getInstance()
		try {
			const created = await ProfileService.addDestinations(profileId, destinations)
			EventsService.emitSuccess(translation.translate('web.profile.toast.add_success'))
			ProfileMetric.addProfileDestinations(created)
			return created
		} catch (e) {
			console.error(e)
			EventsService.emitError(translation.translate('web.profile.toast.add_error'))
			return []
		}
	}

	private static isCurrentUser(profileId: string): boolean {
		const userProfile: string | undefined = this.profileStore.profile?.id
		return userProfile === profileId
	}

	static visitedIsoCodes(visited: ProfileDestination[]) {
		return this.obtainUniqField(visited, 'countryIso')
	}

	static visitedStamps(visited: ProfileDestination[]) {
		return this.obtainUniqField(visited, 'countryStampUrl')
	}

	private static readonly mapWishlistColor: string = 'brown'
	private static readonly mapVisitedColor: string = 'green'

	static getGeochartData(
		destinations: ProfileDestination[],
		type: ProfileDestinationType
	): Record<string, string> {
		const uniqueWishIsoCodes = this.obtainUniqField(destinations, 'countryIso')
		const color = type === 'visited' ? this.mapVisitedColor : this.mapWishlistColor
		return {
			...Object.fromEntries(uniqueWishIsoCodes.map((key) => [key, color])),
		}
	}

	static mapCorrelation(): Record<string, { code: number; color: string }> {
		return {
			[this.mapWishlistColor]: { code: 0, color: ColorPalette.getColor('secondary', 500) },
			[this.mapVisitedColor]: { code: 100, color: ColorPalette.getColor('primary', 500) },
		}
	}

	public static obtainUniqField<T>(list: T[], field: keyof T): string[] {
		return [...new Set(list.map((item) => item[field]).filter(Boolean) as string[])]
	}

	private static get profileStore() {
		return useProfileStore()
	}
}
